import type { GridState } from "ag-grid-community";

const CARRIER_COL_PREFIX = "carrier:";

/**
 * Rewrites a persisted `columnOrder` so its carrier columns follow
 * `carrierOrder` (the order applied to all SBS pages from Quotes), leaving
 * every non-carrier column (label, period, etc.) in its persisted slot.
 *
 * AG Grid restores `initialState.columnOrder` over the colDef order on mount,
 * so without this a stale persisted carrier order overrides the applied one
 * until the grid next remounts. Carriers not named in `carrierOrder` keep
 * their existing relative order and are appended after the named ones.
 *
 * Returns the state unchanged when there is no persisted column order or no
 * applied carrier order.
 */
export const alignPersistedCarrierColumnOrder = (
  state: GridState | undefined,
  carrierOrder: readonly string[] | undefined,
): GridState | undefined => {
  const orderedColIds = state?.columnOrder?.orderedColIds;
  if (!state || !orderedColIds || !carrierOrder || carrierOrder.length === 0) {
    return state;
  }

  const carrierColIds = orderedColIds.filter((colId) =>
    colId.startsWith(CARRIER_COL_PREFIX),
  );
  if (carrierColIds.length === 0) {
    return state;
  }

  const rank = new Map<string, number>();
  carrierOrder.forEach((id, index) =>
    rank.set(`${CARRIER_COL_PREFIX}${id}`, index),
  );

  const reorderedCarrierColIds = carrierColIds
    .map((colId, index) => ({ colId, index }))
    .sort((a, b) => {
      const rankA = rank.get(a.colId) ?? Infinity;
      const rankB = rank.get(b.colId) ?? Infinity;
      return rankA === rankB ? a.index - b.index : rankA - rankB;
    })
    .map(({ colId }) => colId);

  let next = 0;
  const alignedColIds = orderedColIds.map((colId) =>
    colId.startsWith(CARRIER_COL_PREFIX)
      ? reorderedCarrierColIds[next++]
      : colId,
  );

  return {
    ...state,
    columnOrder: { ...state.columnOrder, orderedColIds: alignedColIds },
  };
};
